import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

/**
 * Coach: Detailed view of a single apprentice.
 * Route: /coach/azubi/:id
 */
const CoachAzubiDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { fetchWithAuth } = useContext(AuthContext);
    const [data, setData] = useState(null);
    const [notes, setNotes] = useState([]);
    const [newNote, setNewNote] = useState('');
    const [tab, setTab] = useState('progress');
    const [loading, setLoading] = useState(true);

    useEffect(() => { load(); }, [id]);

    const load = async () => {
        setLoading(true);
        try {
            const [detailRes, notesRes] = await Promise.all([
                fetchWithAuth(`/api/coach/apprentice/${id}/`),
                fetchWithAuth(`/api/coach/notes/${id}/`),
            ]);
            setData(await detailRes.json());
            setNotes(await notesRes.json());
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const saveNote = async () => {
        if (!newNote.trim()) return;
        const res = await fetchWithAuth(`/api/coach/notes/${id}/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: newNote }),
        });
        if (res.ok) {
            const note = await res.json();
            setNotes(prev => [note, ...prev]);
            setNewNote('');
        }
    };

    const signWeek = async (weekId, status = 'SIGNED', comment = '') => {
        const res = await fetchWithAuth(`/api/coach/reports/${weekId}/review/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status, comment }),
        });
        if (res.ok) load();
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-[60vh]">
            <div className="w-8 h-8 border-4 border-plantlix-500 border-t-transparent rounded-full animate-spin" />
        </div>
    );

    if (!data || data.error) return (
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
            <p className="text-4xl mb-4">🔒</p>
            <p className="text-gray-500 dark:text-gray-400">{data?.error || 'Kein Zugriff'}</p>
            <button onClick={() => navigate('/coach')} className="mt-4 px-4 py-2 rounded-xl bg-plantlix-500 text-white text-sm font-semibold">Zurück</button>
        </div>
    );

    const { stats, training_weeks } = data;
    const totalPlants = stats.learned_count + stats.in_progress_count + stats.not_learned_count;
    const progressPct = totalPlants > 0 ? Math.round((stats.learned_count / totalPlants) * 100) : 0;

    const TABS = [
        { key: 'progress', label: '📊 Lernfortschritt' },
        { key: 'reports',  label: '📋 Berichtshefte' },
        { key: 'notes',    label: '📝 Notizen' },
    ];

    return (
        <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
            {/* Back + Header */}
            <button onClick={() => navigate('/coach')} className="text-sm text-gray-500 dark:text-gray-400 hover:text-plantlix-500 transition flex items-center gap-1">
                ← Zurück zum Dashboard
            </button>

            <div className="flex items-center gap-4">
                {stats.avatar ? (
                    <img src={stats.avatar} className="w-14 h-14 rounded-full object-cover" alt="" />
                ) : (
                    <div className="w-14 h-14 rounded-full bg-plantlix-100 dark:bg-plantlix-900/30 flex items-center justify-center text-plantlix-600 dark:text-plantlix-400 font-bold text-xl">
                        {(stats.first_name?.[0] || stats.username?.[0] || '?').toUpperCase()}
                    </div>
                )}
                <div>
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                        {stats.first_name ? `${stats.first_name} ${stats.last_name}` : stats.username}
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Zuletzt aktiv: {stats.last_login_display}</p>
                </div>
            </div>

            {/* Stats cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                    { label: 'Gelernt', val: stats.learned_count, emoji: '✅', color: 'emerald' },
                    { label: 'In Arbeit', val: stats.in_progress_count, emoji: '📖', color: 'amber' },
                    { label: 'Streak', val: `${stats.streak} Tage`, emoji: '🔥', color: 'orange' },
                    { label: 'Offene Berichte', val: stats.pending_reports, emoji: '📋', color: stats.pending_reports > 0 ? 'red' : 'gray' },
                ].map((s, i) => (
                    <div key={i} className="bg-white dark:bg-[#1a1a1a] border border-gray-100 dark:border-white/5 rounded-xl p-4 text-center">
                        <p className="text-2xl mb-1">{s.emoji}</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-white">{s.val}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{s.label}</p>
                    </div>
                ))}
            </div>

            {/* Progress bar */}
            <div className="bg-white dark:bg-[#1a1a1a] border border-gray-100 dark:border-white/5 rounded-xl p-4">
                <div className="flex justify-between text-sm mb-2">
                    <span className="font-semibold text-gray-700 dark:text-gray-300">Prüfungsreife</span>
                    <span className="font-bold text-plantlix-500">{progressPct}%</span>
                </div>
                <div className="h-3 rounded-full bg-gray-100 dark:bg-white/10 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-plantlix-400 to-plantlix-500 transition-all" style={{ width: `${progressPct}%` }} />
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-gray-200 dark:border-white/10 pb-1">
                {TABS.map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)}
                        className={`px-4 py-2 rounded-t-xl text-sm font-semibold transition ${tab === t.key
                            ? 'bg-plantlix-50 dark:bg-plantlix-900/20 text-plantlix-600 dark:text-plantlix-400 border-b-2 border-plantlix-500'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                        }`}>
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Tab content: Lernfortschritt */}
            {tab === 'progress' && (
                <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-3 text-center">
                        <div className="bg-plantlix-50 dark:bg-plantlix-900/20 rounded-xl p-4">
                            <p className="text-2xl font-bold text-plantlix-600 dark:text-plantlix-400">{stats.learned_count}</p>
                            <p className="text-xs text-gray-500">Gelernt</p>
                        </div>
                        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4">
                            <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{stats.in_progress_count}</p>
                            <p className="text-xs text-gray-500">In Arbeit</p>
                        </div>
                        <div className="bg-gray-50 dark:bg-white/5 rounded-xl p-4">
                            <p className="text-2xl font-bold text-gray-600 dark:text-gray-400">{stats.not_learned_count}</p>
                            <p className="text-xs text-gray-500">Nicht begonnen</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Tab content: Berichtshefte */}
            {tab === 'reports' && (
                <div className="space-y-3">
                    {training_weeks.length === 0 && <p className="text-gray-400 text-sm text-center py-8">Noch keine Berichtswochen vorhanden.</p>}
                    {training_weeks.map(w => (
                        <div key={w.id} className="bg-white dark:bg-[#1a1a1a] border border-gray-100 dark:border-white/5 rounded-xl p-4 flex items-center gap-4">
                            <div className="flex-1">
                                <p className="font-bold text-gray-900 dark:text-white text-sm">KW {w.week_number}/{w.year}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{w.daily_report_count}/7 Tage · {w.completion_percentage}%</p>
                            </div>
                            {w.review_status ? (
                                <span className={`px-3 py-1 rounded-lg text-xs font-bold ${w.review_status.status === 'SIGNED' ? 'bg-plantlix-50 dark:bg-plantlix-900/20 text-plantlix-600' : 'bg-amber-50 dark:bg-amber-900/20 text-amber-600'}`}>
                                    {w.review_status.status === 'SIGNED' ? '✅ Abgezeichnet' : '⚠️ Überarbeitung'}
                                </span>
                            ) : (
                                <div className="flex gap-2">
                                    <button onClick={() => signWeek(w.id)} className="px-3 py-1.5 rounded-lg bg-plantlix-500 hover:bg-plantlix-600 text-white text-xs font-bold transition">
                                        ✓ Abzeichnen
                                    </button>
                                    <button onClick={() => signWeek(w.id, 'NEEDS_REVISION')} className="px-3 py-1.5 rounded-lg border border-amber-300 dark:border-amber-700 text-amber-600 dark:text-amber-400 text-xs font-bold hover:bg-amber-50 dark:hover:bg-amber-900/20 transition">
                                        ↩ Überarbeitung
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Tab content: Notizen */}
            {tab === 'notes' && (
                <div className="space-y-4">
                    <div className="flex gap-3">
                        <input
                            value={newNote}
                            onChange={e => setNewNote(e.target.value)}
                            placeholder="Private Notiz hinzufügen…"
                            onKeyDown={e => e.key === 'Enter' && saveNote()}
                            className="flex-1 px-4 py-3 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-plantlix-400"
                        />
                        <button onClick={saveNote} className="px-5 py-3 rounded-xl bg-plantlix-500 hover:bg-plantlix-600 text-white font-bold text-sm transition">
                            Speichern
                        </button>
                    </div>
                    {notes.length === 0 && <p className="text-gray-400 text-sm text-center py-6">Noch keine Notizen.</p>}
                    {notes.map(n => (
                        <div key={n.id} className="bg-white dark:bg-[#1a1a1a] border border-gray-100 dark:border-white/5 rounded-xl p-4">
                            <p className="text-gray-800 dark:text-gray-200 text-sm">{n.text}</p>
                            <p className="text-xs text-gray-400 mt-2">{new Date(n.created_at).toLocaleString('de-DE')}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CoachAzubiDetail;
